import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import CustomModal from "./customModal";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import { useSelector } from "react-redux";

export default function CategoryDepartmentsModal({
  showModal,
  setShowModal,
  onSubmit,
  selectedGroups,
  setSelectedGroups,
  subtitle = "Select category department",
}) {
  const compID = useSelector((state) => state.auth.user).userData?.compID;
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const [categoryDepartments, setCategoryDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [tempSelectedDepartment, setTempSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const isDarkMode = useDarkMode();

  const themeColor = useThemeColor();

  const initializeSelected = (selected) => {
    if (!selected) return null;
    if (Array.isArray(selected)) {
      return selected.length > 0 ? selected[0] : null;
    }
    return selected;
  };

  const fetchCategoryDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_DEPARTMENTS, {
        params: {
          compID,
          Search: searchTerm || "",
          SortBy: "cD_Name",
          SortOrder: "ASC",
        },
      });

      const departmentData = response.data.data || [];
      if (Array.isArray(departmentData)) {
        setCategoryDepartments(departmentData);
        setFilteredDepartments(departmentData);
      } else {
        setCategoryDepartments([]);
        setFilteredDepartments([]);
      }
    } catch (error) {
      setCategoryDepartments([]);
      setFilteredDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [compID, searchTerm]);

  useEffect(() => {
    if (showModal) {
      fetchCategoryDepartments();
    }
  }, [showModal, fetchCategoryDepartments]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredDepartments(categoryDepartments);
    } else {
      const filtered = categoryDepartments.filter((dept) =>
        dept.cD_Name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
      setFilteredDepartments(filtered);
    }
  }, [searchTerm, categoryDepartments]);

  useEffect(() => {
    setTempSelectedDepartment(initializeSelected(selectedGroups));
  }, [selectedGroups, showModal]);

  const handleDepartmentSelection = (department) => {
    if (tempSelectedDepartment?.cD_ID === department.cD_ID) {
      setTempSelectedDepartment(null);
    } else {
      setTempSelectedDepartment(department);
    }
  };

  const handleSubmit = () => {
    if (typeof onSubmit === "function") {
      onSubmit(tempSelectedDepartment);
    }
    setSelectedGroups(tempSelectedDepartment);
    setShowModal(false);
  };

  return (
    <div>
      <CustomModal
        isOpen={showModal}
        title="Select Category Department"
        subtitle={subtitle}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        showButton={true}
        showCancelButton={true}
        showSubmitButton={true}
        submitButtonText="Done"
        isCentered={true}
        modalSize="modal-lg"
        themeColor={themeColor}
      >
        <div className="mb-4">
          <div className="position-relative">
            <input
              type="text"
              className="form-control form-control-lg rounded-pill border-2"
              placeholder="🔍 Search category departments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: "45px",
                borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
                color: isDarkMode ? '#e2e8f0' : '#495057',
                transition: "all 0.3s ease"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = themeColor;
                e.target.style.boxShadow = `0 0 0 0.2rem ${themeColor}25`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? '#4a5568' : '#e9ecef';
                e.target.style.boxShadow = "none";
              }}
            />
            <i 
              className="fa fa-search position-absolute"
              style={{
                left: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "16px",
                color: isDarkMode ? '#a0aec0' : '#6c757d',
                pointerEvents: "none"
              }}
            />
            {searchTerm && (
              <button
                className="btn btn-link position-absolute p-0"
                onClick={() => setSearchTerm("")}
                style={{
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "16px",
                  color: isDarkMode ? '#a0aec0' : '#6c757d'
                }}
              >
                <i className="fa fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {tempSelectedDepartment && (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2">
              <span 
                className="badge rounded-pill px-3 py-2"
                style={{
                  backgroundColor: `${themeColor}30`,
                  color: themeColor
                }}
              >
                <i className="fa fa-check-circle me-1"></i>
                1 Department Selected
              </span>
              <button
                className="btn btn-outline-secondary btn-sm rounded-pill"
                style={{
                  borderColor: isDarkMode ? '#4a5568' : '#e9ecef',
                  color: isDarkMode ? '#e2e8f0' : '#6c757d'
                }}
                onClick={() => setTempSelectedDepartment(null)}
              >
                <i className="fa fa-times me-1"></i>
                Clear
              </button>
            </div>
          </div>
        )}

        <div 
          className="border rounded-3 p-3"
          style={{ 
            height: "350px", 
            overflowY: "auto",
            backgroundColor: isDarkMode ? '#2d3748' : '#f8f9fa',
            borderColor: isDarkMode ? '#4a5568' : '#e9ecef'
          }}
        >
          {loading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <div 
                  className="spinner-border mb-3" 
                  style={{ color: themeColor }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                  Loading departments...
                </p>
              </div>
            </div>
          ) : filteredDepartments.length > 0 ? (
            <div className="row g-3">
              {filteredDepartments.map((department) => {
                const isSelected = tempSelectedDepartment?.cD_ID === department.cD_ID;
                return (
                  <div key={department.cD_ID} className="col-md-6">
                    <div 
                      className={`card h-100 cursor-pointer border-2 transition-all ${
                        isSelected 
                          ? 'bg-opacity-10' 
                          : 'border-light bg-white'
                      }`}
                      style={{
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        borderRadius: "12px",
                        borderColor: isSelected ? themeColor : (isDarkMode ? '#4a5568' : '#e9ecef'),
                        backgroundColor: isSelected 
                          ? `${themeColor}15` 
                          : (isDarkMode ? '#2d3748' : '#ffffff')
                      }}
                      onClick={() => handleDepartmentSelection(department)}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = themeColor;
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}15`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = isDarkMode ? '#4a5568' : '#e9ecef';
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "none";
                        }
                      }}
                    >
                      <div className="card-body d-flex align-items-center justify-content-between p-3">
                        <div className="d-flex align-items-center">
                          <div 
                            className={`rounded-circle d-flex align-items-center justify-content-center me-3`}
                            style={{
                              width: "40px",
                              height: "40px",
                              fontSize: "16px",
                              transition: "all 0.3s ease",
                              backgroundColor: isSelected 
                                ? themeColor 
                                : (isDarkMode ? '#4a5568' : '#f8f9fa'),
                              color: isSelected 
                                ? '#ffffff' 
                                : (isDarkMode ? '#a0aec0' : '#6c757d')
                            }}
                          >
                            <i className={`fa ${isSelected ? 'fa-check' : 'fa-building'}`}></i>
                          </div>
                          <div>
                            <h6 
                              className={`mb-0 fw-semibold`}
                              style={{
                                color: isSelected 
                                  ? themeColor 
                                  : (isDarkMode ? '#e2e8f0' : '#212529')
                              }}
                            >
                              {department.cD_Name}
                            </h6>
                            <small style={{ 
                              color: isDarkMode ? '#a0aec0' : '#6c757d'
                            }}>
                              Department
                            </small>
                          </div>
                        </div>
                        <div className="form-check" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="radio"
                            name="department-radio"
                            className="form-check-input"
                            checked={isSelected}
                            onChange={() => handleDepartmentSelection(department)}
                            id={`department-radio-${department.cD_ID}`}
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                              borderColor: isDarkMode ? '#718096' : '#ced4da',
                              backgroundColor: isSelected ? themeColor : 'transparent'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <i 
                  className="fa fa-search fa-3x mb-3"
                  style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}
                ></i>
                <h5 style={{ color: isDarkMode ? '#e2e8f0' : '#6c757d' }}>
                  No departments found
                </h5>
                <p style={{ color: isDarkMode ? '#a0aec0' : '#6c757d' }}>
                  {searchTerm.length > 0 ? `No departments match "${searchTerm}"` : "No departments available"}
                </p>
              </div>
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
}